import { useEffect, useState } from "react";
import { Copy, Check, Send, ArrowRight, Pencil } from "lucide-react";

type Destination = "linkedin" | "twitter" | "email" | "imessage" | "slack";

const ANIMATED_DESTINATIONS: Destination[] = ["linkedin", "twitter", "email", "imessage", "slack"];

const DEST_LABELS: Record<Destination, string> = {
  linkedin: "LinkedIn",
  twitter: "Twitter / X",
  email: "Email",
  imessage: "iMessage",
  slack: "Slack",
};

const DEST_ICONS: Record<Destination, string> = {
  linkedin: "💼",
  twitter: "🐦",
  email: "✉️",
  imessage: "💬",
  slack: "⚡",
};

const DEST_CONTENT: Record<Destination, { before: string; highlight: string; after: string }> = {
  linkedin: {
    before: "Hot take: the best career move I made wasn't a promotion. It was finally writing like myself instead of every other LinkedIn post. ",
    highlight: "Authenticity > polish.",
    after: "",
  },
  twitter: {
    before: "hot take: authenticity beats polish every time. the posts that sound like you outperform the ones that sound like everyone else. ",
    highlight: "stop performing. just write.",
    after: " 🧵",
  },
  email: {
    before: "Hey — just wanted to share something I've been thinking about. The content that's landed for me hasn't been the \"professional\" stuff. ",
    highlight: "It's been the honest stuff.",
    after: " Thought you might relate.",
  },
  imessage: {
    before: "yo write something quick about ",
    highlight: "showing up authentically online?",
    after: "",
  },
  slack: {
    before: "Hey team 👋 quick thought: our most-engaged posts are always the ones that sound like actual humans wrote them. ",
    highlight: "Let's lean into that this week.",
    after: "",
  },
};

const ALL_PLATFORM_PILLS = [
  { icon: "💼", label: "LinkedIn" },
  { icon: "🐦", label: "Twitter / X" },
  { icon: "📸", label: "Instagram" },
  { icon: "📘", label: "Facebook" },
  { icon: "🤖", label: "Reddit" },
  { icon: "✉️", label: "Email" },
  { icon: "💬", label: "iMessage" },
  { icon: "🎙", label: "Substack" },
  { icon: "👔", label: "Slack" },
  { icon: "🎮", label: "Discord" },
];

type Phase = "idle" | "editing" | "clicking" | "flying" | "arrived" | "exiting";

function LinkedInMock({ visible }: { visible: boolean }) {
  const { before, highlight, after } = DEST_CONTENT.linkedin;
  const text = before + highlight + after;
  return (
    <div
      className="rounded-2xl border border-white/10 glass-panel p-4 transition-all duration-500"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0) scale(1)" : "translateY(16px) scale(0.97)",
        background: "hsl(222 44% 7% / 0.9)",
      }}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white text-xs font-bold shadow-lg">
          A
        </div>
        <div>
          <p className="text-[13px] font-semibold text-white leading-tight">Alex Rivera</p>
          <p className="text-[11px] text-slate-400">Senior Product Manager · 2m ago</p>
        </div>
        <div className="ml-auto">
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-blue-500/15 text-blue-400 border border-blue-500/25">
            💼 LinkedIn
          </span>
        </div>
      </div>
      <p className="text-[13px] text-slate-200 leading-relaxed mb-3">{text}</p>
      <div className="flex items-center gap-4 text-[11px] text-slate-500 border-t border-white/8 pt-2">
        <span>👍 42 reactions</span>
        <span>💬 8 comments</span>
        <span>🔁 Share</span>
      </div>
    </div>
  );
}

function TwitterMock({ visible }: { visible: boolean }) {
  const { before, highlight, after } = DEST_CONTENT.twitter;
  const text = before + highlight + after;
  return (
    <div
      className="rounded-2xl border border-white/10 glass-panel p-4 transition-all duration-500"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0) scale(1)" : "translateY(16px) scale(0.97)",
        background: "hsl(222 44% 7% / 0.9)",
      }}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-slate-600 to-slate-800 flex items-center justify-center text-white text-xs font-bold shadow-lg border border-white/10">
          A
        </div>
        <div>
          <p className="text-[13px] font-semibold text-white leading-tight">Alex Rivera</p>
          <p className="text-[11px] text-slate-400">@alexrivera · 1m</p>
        </div>
        <div className="ml-auto">
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-slate-500/15 text-slate-300 border border-slate-500/25">
            🐦 Twitter / X
          </span>
        </div>
      </div>
      <p className="text-[13px] text-slate-200 leading-relaxed mb-3">{text}</p>
      <div className="flex items-center gap-4 text-[11px] text-slate-500 border-t border-white/8 pt-2">
        <span>♥ 218</span>
        <span>🔁 57 retweets</span>
        <span>💬 34 replies</span>
      </div>
    </div>
  );
}

function EmailMock({ visible }: { visible: boolean }) {
  const { before, highlight, after } = DEST_CONTENT.email;
  const text = before + highlight + after;
  return (
    <div
      className="rounded-2xl border border-white/10 glass-panel overflow-hidden transition-all duration-500"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0) scale(1)" : "translateY(16px) scale(0.97)",
        background: "hsl(222 44% 7% / 0.9)",
      }}
    >
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/8 bg-white/[0.02]">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
        </div>
        <span className="text-[11px] text-slate-400 ml-2">New Message</span>
        <div className="ml-auto">
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/25">
            ✉️ Email
          </span>
        </div>
      </div>
      <div className="p-4">
        <div className="text-[11px] text-slate-500 mb-1">To: <span className="text-slate-300">sarah@company.com</span></div>
        <div className="text-[11px] text-slate-500 mb-3">Subject: <span className="text-slate-300">Quick thought I had to share</span></div>
        <p className="text-[13px] text-slate-200 leading-relaxed">{text}</p>
        <div className="flex items-center gap-2 mt-3 pt-2.5 border-t border-white/8">
          <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500 text-white text-[11px] font-semibold">
            <Send className="w-3 h-3" /> Send
          </button>
          <span className="text-[11px] text-slate-500">Just now · Sent</span>
        </div>
      </div>
    </div>
  );
}

function IMessageMock({ visible }: { visible: boolean }) {
  const { before, highlight, after } = DEST_CONTENT.imessage;
  const text = before + highlight + after;
  const reply = "lol yes. write something about showing up authentically online?";
  return (
    <div
      className="rounded-2xl border border-white/10 glass-panel overflow-hidden transition-all duration-500"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0) scale(1)" : "translateY(16px) scale(0.97)",
        background: "hsl(222 44% 7% / 0.9)",
      }}
    >
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/8 bg-white/[0.02]">
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-[10px] font-bold">
          J
        </div>
        <div>
          <p className="text-[12px] font-semibold text-white">Jordan</p>
          <p className="text-[10px] text-green-400">iMessage</p>
        </div>
        <div className="ml-auto">
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-purple-500/15 text-purple-400 border border-purple-500/25">
            💬 iMessage
          </span>
        </div>
      </div>
      <div className="p-4 space-y-2">
        <div className="flex justify-start">
          <div className="max-w-[75%] bg-white/10 rounded-2xl rounded-tl-sm px-3 py-2">
            <p className="text-[12px] text-slate-200">{text}</p>
          </div>
        </div>
        <div className="flex justify-end">
          <div
            className="max-w-[80%] rounded-2xl rounded-tr-sm px-3 py-2 text-[12px] text-white leading-relaxed"
            style={{ background: "linear-gradient(135deg, hsl(188 90% 42%), hsl(205 88% 52%))" }}
          >
            {reply}
          </div>
        </div>
        <div className="flex justify-end">
          <p className="text-[10px] text-slate-500">Delivered · Just now</p>
        </div>
      </div>
    </div>
  );
}

function SlackMock({ visible }: { visible: boolean }) {
  const { before, highlight, after } = DEST_CONTENT.slack;
  const text = before + highlight + after;
  return (
    <div
      className="rounded-2xl border border-white/10 glass-panel overflow-hidden transition-all duration-500"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0) scale(1)" : "translateY(16px) scale(0.97)",
        background: "hsl(222 44% 7% / 0.9)",
      }}
    >
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/8 bg-white/[0.02]">
        <div className="w-5 h-5 rounded bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center text-white text-[9px] font-bold">
          #
        </div>
        <span className="text-[11px] text-slate-300 font-medium">content-strategy</span>
        <div className="ml-auto">
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-violet-500/15 text-violet-300 border border-violet-500/25">
            ⚡ Slack
          </span>
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-start gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white text-[11px] font-bold shrink-0 shadow">
            A
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-[12px] font-bold text-white">Alex Rivera</span>
              <span className="text-[10px] text-slate-500">Today at 10:14 AM</span>
            </div>
            <p className="text-[12px] text-slate-200 leading-relaxed">{text}</p>
            <div className="flex items-center gap-2 mt-2">
              <span className="text-[11px] bg-white/8 border border-white/10 rounded px-1.5 py-0.5">👍 3</span>
              <span className="text-[11px] bg-white/8 border border-white/10 rounded px-1.5 py-0.5">🔥 2</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ShareLoopSection() {
  const [destIndex, setDestIndex] = useState(0);
  const [phase, setPhase] = useState<Phase>("idle");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let t1: ReturnType<typeof setTimeout>;
    let t2: ReturnType<typeof setTimeout>;
    let t3: ReturnType<typeof setTimeout>;
    let t4: ReturnType<typeof setTimeout>;
    let t5: ReturnType<typeof setTimeout>;

    const runCycle = () => {
      setPhase("idle");
      setCopied(false);

      // idle → editing at 900ms
      t1 = setTimeout(() => {
        setPhase("editing");
      }, 900);

      // editing → clicking (copy) at 2200ms
      t2 = setTimeout(() => {
        setPhase("clicking");
        setCopied(true);
      }, 2200);

      // clicking → flying at 3000ms
      t3 = setTimeout(() => {
        setPhase("flying");
      }, 3000);

      // flying → arrived at 3600ms
      t4 = setTimeout(() => {
        setPhase("arrived");
      }, 3600);

      // arrived → exiting at 6600ms (3s of destination visible)
      t5 = setTimeout(() => {
        setPhase("exiting");
        setTimeout(() => {
          setCopied(false);
          setDestIndex((i) => (i + 1) % ANIMATED_DESTINATIONS.length);
          runCycle();
        }, 500);
      }, 6600);
    };

    runCycle();
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
      clearTimeout(t5);
    };
  }, []);

  const dest = ANIMATED_DESTINATIONS[destIndex];
  const destArrived = phase === "arrived";
  const isEditing = phase === "editing";
  const { before, highlight, after } = DEST_CONTENT[dest];

  return (
    <section className="py-20 px-4 sm:px-6 relative overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 70% 50% at 50% 50%, hsl(205 88% 52% / 0.07) 0%, transparent 65%)",
        }}
      />
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-xs text-cyan-400 font-semibold uppercase tracking-widest mb-3" style={{ filter: "drop-shadow(0 0 6px hsl(188 90% 42% / 0.55))" }}>
            Generate. Refine. Send.
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Write in your voice —{" "}
            <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
              then send it anywhere
            </span>
          </h2>
          <p className="text-slate-400 text-lg max-w-lg mx-auto">
            ScribPost generates content in your exact voice. Tweak it with one click, then paste it anywhere — LinkedIn, email, Slack, iMessage, and more.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 items-center max-w-4xl mx-auto">
          {/* Source card */}
          <div className="space-y-3">
            <p className="text-xs text-slate-400 uppercase tracking-widest font-medium mb-2">
              Your ScribPost output
            </p>
            <div
              className="rounded-2xl border glass-panel p-5 relative transition-all duration-500"
              style={{
                background: "hsl(222 44% 6% / 0.95)",
                boxShadow: isEditing
                  ? "0 0 40px hsl(188 90% 42% / 0.16)"
                  : "0 0 40px hsl(188 90% 42% / 0.08)",
                borderColor: isEditing
                  ? "hsl(188 90% 42% / 0.45)"
                  : "hsl(188 90% 42% / 0.2)",
              }}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-cyan-400" style={{ boxShadow: "0 0 6px hsl(188 90% 42%)" }} />
                  <span className="text-[11px] text-cyan-400 font-medium">ScribPost · Your voice</span>
                </div>
                {isEditing && (
                  <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-cyan-500/12 border border-cyan-500/30 transition-all duration-300">
                    <Pencil className="w-2.5 h-2.5 text-cyan-400" />
                    <span className="text-[10px] text-cyan-400 font-medium">Refining…</span>
                  </div>
                )}
              </div>

              <p className="text-[13px] text-slate-200 leading-relaxed mb-4 transition-all duration-300">
                {before}
                <span
                  className="transition-all duration-300 rounded-sm px-0.5"
                  style={{
                    background: isEditing ? "hsl(188 90% 42% / 0.22)" : "transparent",
                    color: isEditing ? "hsl(188 90% 80%)" : "inherit",
                  }}
                >
                  {highlight}
                </span>
                {after}
                {isEditing && (
                  <span className="inline-block w-0.5 h-3.5 bg-cyan-400 ml-0.5 animate-pulse align-middle" />
                )}
              </p>

              <div className="flex items-center gap-2">
                <button
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-300"
                  style={{
                    background: copied
                      ? "linear-gradient(135deg, hsl(142 70% 45%), hsl(142 70% 38%))"
                      : "linear-gradient(135deg, hsl(188 90% 42%), hsl(205 88% 52%))",
                    transform: phase === "clicking" ? "scale(0.93)" : "scale(1)",
                    boxShadow: copied
                      ? "0 0 16px hsl(142 70% 45% / 0.5)"
                      : "0 0 16px hsl(188 90% 42% / 0.4)",
                    color: "white",
                  }}
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? "Copied!" : "Copy"}
                </button>
                {(phase === "flying" || phase === "arrived") && (
                  <div className="flex items-center gap-1.5 text-[12px] text-slate-300">
                    <ArrowRight className="w-3.5 h-3.5 text-cyan-400 animate-spin" />
                    <span>Sending to <span className="text-cyan-400 font-medium">{DEST_ICONS[dest]} {DEST_LABELS[dest]}</span></span>
                  </div>
                )}
              </div>
            </div>

            {/* Platform grid — shows the full range of supported destinations */}
            <div>
              <p className="text-[10px] text-slate-600 uppercase tracking-widest font-semibold mb-2">Paste into any of these — and more</p>
              <div className="flex flex-wrap gap-1.5">
                {ALL_PLATFORM_PILLS.map((p) => {
                  const isActive = p.label === DEST_LABELS[dest] && (phase === "arrived" || phase === "flying");
                  return (
                    <div
                      key={p.label}
                      className="flex items-center gap-1 px-2 py-1 rounded-full border text-[11px] font-medium transition-all duration-300"
                      style={{
                        background: isActive ? "hsl(188 90% 42% / 0.15)" : "transparent",
                        borderColor: isActive ? "hsl(188 90% 42% / 0.5)" : "hsl(222 44% 22% / 0.5)",
                        color: isActive ? "hsl(188 90% 70%)" : "hsl(215 25% 50%)",
                        boxShadow: isActive ? "0 0 10px hsl(188 90% 42% / 0.2)" : "none",
                      }}
                    >
                      <span>{p.icon}</span>
                      {p.label}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Destination mock */}
          <div className="space-y-3">
            <p className="text-xs text-slate-400 uppercase tracking-widest font-medium mb-2">
              Arrives as
            </p>
            <div className="min-h-[220px]">
              {dest === "linkedin" && <LinkedInMock visible={destArrived} />}
              {dest === "twitter" && <TwitterMock visible={destArrived} />}
              {dest === "email" && <EmailMock visible={destArrived} />}
              {dest === "imessage" && <IMessageMock visible={destArrived} />}
              {dest === "slack" && <SlackMock visible={destArrived} />}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
